
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { parsePhoneNumber } from 'libphonenumber-js';
import { FormValues, formSchema } from "./types";
import { countries } from "./countries";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface PhoneNumberFormProps {
  onSubmit: (values: FormValues) => Promise<void>;
  isSubmitting: boolean;
}

const PhoneNumberForm = ({ onSubmit, isSubmitting }: PhoneNumberFormProps) => {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      countryCode: "",
      phoneNumber: "",
      email: "",
      receiveUpdates: true,
    },
  });
  
  const formatPhoneNumber = (value: string, countryCode: string) => {
    try {
      const fullNumber = `${countryCode}${value}`;
      const parsed = parsePhoneNumber(fullNumber);
      if (parsed) {
        return parsed.formatNational().replace(/[^\d]/g, '');
      }
    } catch {
      return value;
    }
    return value;
  };

  const handlePhoneNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    onChange: (value: string) => void,
    countryCode: string
  ) => {
    let value = event.target.value.replace(/[^\d]/g, '');
    const formattedValue = formatPhoneNumber(value, countryCode);
    onChange(formattedValue);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pt-4">
        <FormField
          control={form.control}
          name="countryCode"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <SelectTrigger className="w-full bg-white border-2 border-gray-200 hover:border-deepseek-primary focus:border-deepseek-primary transition-colors">
                    <SelectValue placeholder="Select your country" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {countries.map((country, index) => (
                      <SelectItem 
                        key={`country-${country.code}-${index}`}
                        value={country.code}
                      >
                        {country.name} ({country.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="phoneNumber"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  placeholder="Enter your phone number (numbers only)"
                  {...field}
                  onChange={(e) => 
                    handlePhoneNumberChange(
                      e, 
                      field.onChange,
                      form.getValues("countryCode")
                    )
                  }
                  className="text-center bg-white border-2 border-gray-200 hover:border-deepseek-primary focus:border-deepseek-primary focus-visible:ring-0 focus-visible:ring-offset-0 transition-colors"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  type="email"
                  placeholder="Enter your email address"
                  {...field}
                  className="text-center bg-white border-2 border-gray-200 hover:border-deepseek-primary focus:border-deepseek-primary focus-visible:ring-0 focus-visible:ring-offset-0 transition-colors"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="receiveUpdates"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-2 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  className="data-[state=checked]:bg-deepseek-primary data-[state=checked]:border-deepseek-primary mt-1"
                />
              </FormControl>
              <div className="text-sm leading-tight text-gray-600">
                Keep me updated about DeepSeek news and features
              </div>
            </FormItem>
          )}
        />
        <div className="flex justify-center pt-2">
          <Button
            type="submit"
            size="lg"
            className="bg-[#2ab540] hover:bg-[#239435] text-white font-semibold px-8 py-4 text-lg rounded-[999px] transition-all duration-200 transform hover:scale-105 w-full shadow-lg"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Joining..." : "Join the Waitlist"}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default PhoneNumberForm;
